
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Layout from '@/components/Layout';
import { motion } from 'framer-motion';
import { 
  ChevronRight, Utensils, Calendar, Calculator, Heart, 
  ArrowRight, User, ShoppingCart, Clock, 
  Users, ListPlus, LayoutDashboard, Edit, 
  Award, CheckCircle, XCircle, AlertCircle, Sparkles,
  Trophy, ArrowUpRight, Droplet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useRecipes } from '@/contexts/RecipesContext';
import DateDisplay from '@/components/DateDisplay';
import { useIsMobile } from '@/hooks/use-mobile';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getVitalScoreHistory, calculateVitalScore } from '@/utils/vitalScoreUtils';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import HomeChallenges from '@/components/HomeChallenges';
import { toast } from 'sonner';
import ImpactBanner from '@/components/ImpactBanner';

const LoggedOutHero = () => (
  <section className="relative bg-gradient-to-r from-vegana-primary to-vegana-secondary overflow-hidden">
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="absolute inset-0 bg-black/30 z-0"
    />
    <div className="container mx-auto px-4 py-24 md:py-32 flex items-center relative z-10">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2 }}
        className="text-white max-w-2xl mx-auto text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="flex justify-center mb-6"
        >
          <div className="bg-white p-3 rounded-xl inline-block shadow-xl">
            <img 
              src="/lovable-uploads/c0f6fe53-8b80-4c8d-b59a-d556679f7965.png" 
              alt="Guía Vegana Logo" 
              className="h-24 md:h-32"
            />
          </div>
        </motion.div>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="text-lg md:text-xl mb-8 text-white/90"
        >
          Tu compañero diario para una alimentación vegana nutritiva, con recetas deliciosas, planes de alimentación personalizados, lista de la compra, asesoramiento nutricional disponible 7/7, y una gran comunidad.
        </motion.p>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Link>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button className="bg-white hover:bg-gray-100 text-vegana-primary font-bold text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-shadow">
                Comenzar Ahora
                <ChevronRight size={20} />
              </Button>
            </motion.div>
          </Link>
          <Link to="/login">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button variant="secondary" className="text-white hover:bg-vegana-secondary/90 font-bold text-lg px-8 py-6">
                Iniciar Sesión
              </Button>
            </motion.div>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

const getPersonalizedTip = (user, vitalScore) => {
  if (!user || !vitalScore) return "Recuerda mantener una alimentación equilibrada y mantenerte hidratado durante el día.";
  
  const waterScore = typeof vitalScore.hydrationSun?.waterScore === 'number' && !isNaN(vitalScore.hydrationSun.waterScore) 
    ? vitalScore.hydrationSun.waterScore : 0;
    
  const stepsScore = typeof vitalScore.movement?.stepsScore === 'number' && !isNaN(vitalScore.movement.stepsScore)
    ? vitalScore.movement.stepsScore : 0;
    
  const restScore = typeof vitalScore.energyRest?.restScore === 'number' && !isNaN(vitalScore.energyRest.restScore)
    ? vitalScore.energyRest.restScore : 0;
    
  const nutritionScore = typeof vitalScore.nutrition?.score === 'number' && !isNaN(vitalScore.nutrition.score)
    ? vitalScore.nutrition.score : 0;
  
  if (waterScore < 50) {
    return "Parece que necesitas beber más agua. Recuerda que mantenerte hidratado es esencial para tu bienestar.";
  } else if (stepsScore < 40) {
    return "Intenta caminar un poco más hoy. Incluso pequeños paseos pueden ayudar a mejorar tu energía.";
  } else if (restScore < 50) {
    return "Tu descanso parece bajo. Considera acostarte un poco más temprano esta noche.";
  } else if (nutritionScore < 60) {
    return "Intenta incorporar más alimentos frescos y vegetales en tu dieta para mejorar tu nutrición.";
  } else {
    return "¡Estás haciendo un gran trabajo! Sigue manteniendo estos buenos hábitos.";
  }
};

const getDailyGreeting = () => {
  const greetings = [
    "¡Hoy es un nuevo día para cuidar de ti! 🌱 ¿Vamos a por tu mejor energía?",
    "¡Nuevo día, nuevas oportunidades para sentirte genial! 🌿 ¿Empezamos?",
    "¡Vamos a potenciar tu energía y vitalidad! 💪🔥",
    "¡Qué alegría verte! 🌞 ¿Preparada para un día lleno de vitalidad?",
    "¡Un nuevo amanecer para tu salud! 🌄 ¿Vamos a por todas hoy?"
  ];
  
  const today = new Date().toISOString().split('T')[0];
  const seed = today.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return greetings[seed % greetings.length];
};

const getScoreDisplay = (score) => {
  const safeScore = typeof score === 'number' && !isNaN(score) ? score : 0;
  
  if (safeScore >= 80) {
    return { emoji: "🟢", text: "¡Excelente hoy!", color: "text-emerald-600" };
  } else if (safeScore >= 60) {
    return { emoji: "🟢", text: "¡Vas bien hoy!", color: "text-green-600" };
  } else if (safeScore >= 40) {
    return { emoji: "🟡", text: "Regular, ¡puedes mejorar!", color: "text-amber-600" };
  } else {
    return { emoji: "🔴", text: "Necesita atención", color: "text-red-600" };
  }
};

const LoggedInDashboard = ({ user }) => {
  const isAdmin = user?.role === 'admin';
  const [dailyGreeting] = useState(getDailyGreeting());
  const [currentTime, setCurrentTime] = useState(new Date());
  const isMobile = useIsMobile();
  const [vitalScore, setVitalScore] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const today = format(new Date(), 'yyyy-MM-dd');
  
  // Simplified vital score initialization
  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }
    
    const initializeVitalScore = () => {
      try {
        // Try to get from localStorage first
        const storedVitalScore = localStorage.getItem('currentVitalScore');
        if (storedVitalScore) {
          const parsedScore = JSON.parse(storedVitalScore);
          if (parsedScore && typeof parsedScore.total === 'number') {
            setVitalScore(parsedScore);
            setIsLoading(false);
            return;
          }
        }
        
        // Check history
        const vitalScoreHistory = getVitalScoreHistory();
        const savedVitalScore = vitalScoreHistory[today];
        
        if (savedVitalScore && typeof savedVitalScore === 'number' && !isNaN(savedVitalScore)) {
          const scoreObj = {
            total: savedVitalScore,
            nutrition: { score: 0 },
            movement: { score: 0, stepsScore: 0 },
            energyRest: { score: 0, restScore: 0 },
            hydrationSun: { score: 0, waterScore: 0 },
            weightBmi: { score: 0 }
          };
          setVitalScore(scoreObj);
        } else {
          // Set default score
          const defaultScore = {
            total: 50,
            nutrition: { score: 50 },
            movement: { score: 50, stepsScore: 50 },
            energyRest: { score: 50, restScore: 50 },
            hydrationSun: { score: 50, waterScore: 50 },
            weightBmi: { score: 50 }
          };
          setVitalScore(defaultScore);
        }
      } catch (error) {
        console.error('Error initializing vital score:', error);
        // Set fallback score
        const fallbackScore = {
          total: 50,
          nutrition: { score: 50 },
          movement: { score: 50, stepsScore: 50 },
          energyRest: { score: 50, restScore: 50 },
          hydrationSun: { score: 50, waterScore: 50 },
          weightBmi: { score: 50 }
        };
        setVitalScore(fallbackScore);
      } finally {
        setIsLoading(false);
      }
    };
    
    // Initialize immediately
    initializeVitalScore();
  }, [user?.id, today]);
  
  // Simplified event listeners
  useEffect(() => {
    const handleVitalScoreUpdate = (event) => {
      if (event.detail?.vitalScore) {
        setVitalScore(event.detail.vitalScore);
      }
    };

    const handleStorageChange = (e) => {
      if (e.key === 'currentVitalScore' && e.newValue) {
        try {
          const parsedScore = JSON.parse(e.newValue);
          if (parsedScore && typeof parsedScore.total === 'number') {
            setVitalScore(parsedScore);
          }
        } catch (error) {
          console.error('Error parsing vital score from storage:', error);
        }
      }
    };
    
    window.addEventListener('vital-score-updated', handleVitalScoreUpdate);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('vital-score-updated', handleVitalScoreUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Time update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, []);
  
  // Don't show loading for too long - always provide some content
  if (isLoading && !vitalScore) {
    // Only show loading for a brief moment, then show default content
    setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setVitalScore({
          total: 50,
          nutrition: { score: 50 },
          movement: { score: 50, stepsScore: 50 },
          energyRest: { score: 50, restScore: 50 },
          hydrationSun: { score: 50, waterScore: 50 },
          weightBmi: { score: 50 }
        });
      }
    }, 2000);
    
    return (
      <div className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-24">
            <p>Cargando datos de bienestar...</p>
          </div>
        </div>
      </div>
    );
  }
  
  const scoreTotal = typeof vitalScore?.total === 'number' && !isNaN(vitalScore.total) 
    ? vitalScore.total 
    : 50;
    
  const scoreDisplay = getScoreDisplay(scoreTotal);
  const personalizedTip = getPersonalizedTip(user, vitalScore);

  return (
    <div className="py-8 md:py-12">
      <div className="container mx-auto px-4">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-r from-vegana-secondary/40 to-vegana-primary/30 rounded-2xl p-6 md:p-8 mb-10 shadow-md"
        >
          <h1 className="text-2xl md:text-3xl font-bold text-vegana-primary mb-2">
            ¡Bienvenido{user?.name ? `, ${user.name}` : ''}!
          </h1>
          <p className="text-gray-600 mb-0">
            ¿Qué te gustaría hacer hoy con Guía Vegana?
          </p>
        </motion.div>
        
        {isAdmin && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-6 text-vegana-primary">Panel de Administración</h2>
            <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
              <Link to="/admin/dashboard">
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-md p-4 md:p-6 flex flex-col items-center text-center hover:shadow-lg transition-all border border-gray-100 h-full bg-vegana-accent/20"
                >
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-vegana-accent/40 flex items-center justify-center mb-2 md:mb-4">
                    <LayoutDashboard className="w-5 h-5 md:w-7 md:h-7 text-vegana-primary" />
                  </div>
                  <h3 className="font-medium text-sm md:text-lg mb-1 md:mb-2">Dashboard</h3>
                  <p className="text-gray-500 text-xs md:text-sm">Visión general del sistema</p>
                </motion.div>
              </Link>
              
              <Link to="/admin/usuarios">
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-md p-4 md:p-6 flex flex-col items-center text-center hover:shadow-lg transition-all border border-gray-100 h-full bg-vegana-secondary/20"
                >
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-vegana-secondary/40 flex items-center justify-center mb-2 md:mb-4">
                    <Users className="w-5 h-5 md:w-7 md:h-7 text-vegana-primary" />
                  </div>
                  <h3 className="font-medium text-sm md:text-lg mb-1 md:mb-2">Gestionar Usuarios</h3>
                  <p className="text-gray-500 text-xs md:text-sm">Administra los usuarios de la plataforma</p>
                </motion.div>
              </Link>
              
              <Link to="/admin/recetas">
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-md p-4 md:p-6 flex flex-col items-center text-center hover:shadow-lg transition-all border border-gray-100 h-full bg-vegana-secondary/20"
                >
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-vegana-secondary/40 flex items-center justify-center mb-2 md:mb-4">
                    <ListPlus className="w-5 h-5 md:w-7 md:h-7 text-vegana-primary" />
                  </div>
                  <h3 className="font-medium text-sm md:text-lg mb-1 md:mb-2">Gestionar Recetas</h3>
                  <p className="text-gray-500 text-xs md:text-sm">Crea y edita las recetas de la plataforma</p>
                </motion.div>
              </Link>
            </div>
          </div>
        )}
        
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-vegana-primary">Acceso rápido</h2>
          <div className="grid grid-cols-4 gap-2 md:gap-3">
            <Link to="/recetas">
              <motion.div 
                whileHover={{ y: -3, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white rounded-xl shadow-md p-3 md:p-4 flex flex-col items-center text-center hover:shadow-lg transition-all border border-gray-100 h-full bg-gradient-to-b from-white to-vegana-secondary/10"
              >
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-vegana-secondary/40 flex items-center justify-center mb-2">
                  <Utensils className="w-4 h-4 md:w-5 md:h-5 text-vegana-primary" />
                </div>
                <h3 className="font-medium text-xs md:text-sm">Recetas</h3>
              </motion.div>
            </Link>
            
            <Link to="/menu-semanal">
              <motion.div 
                whileHover={{ y: -3, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white rounded-xl shadow-md p-3 md:p-4 flex flex-col items-center text-center hover:shadow-lg transition-all border border-gray-100 h-full bg-gradient-to-b from-white to-vegana-secondary/10"
              >
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-vegana-secondary/40 flex items-center justify-center mb-2">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-vegana-primary" />
                </div>
                <h3 className="font-medium text-xs md:text-sm">Menú</h3>
              </motion.div>
            </Link>
            
            <Link to="/lista-compra">
              <motion.div 
                whileHover={{ y: -3, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white rounded-xl shadow-md p-3 md:p-4 flex flex-col items-center text-center hover:shadow-lg transition-all border border-gray-100 h-full bg-gradient-to-b from-white to-vegana-secondary/10"
              >
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-vegana-secondary/40 flex items-center justify-center mb-2">
                  <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 text-vegana-primary" />
                </div>
                <h3 className="font-medium text-xs md:text-sm">Compra</h3>
              </motion.div>
            </Link>
            
            <Link to="/perfil">
              <motion.div 
                whileHover={{ y: -3, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white rounded-xl shadow-md p-3 md:p-4 flex flex-col items-center text-center hover:shadow-lg transition-all border border-gray-100 h-full bg-gradient-to-b from-white to-vegana-secondary/10"
              >
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-vegana-secondary/40 flex items-center justify-center mb-2">
                  <User className="w-4 h-4 md:w-5 md:h-5 text-vegana-primary" />
                </div>
                <h3 className="font-medium text-xs md:text-sm">Perfil</h3>
              </motion.div>
            </Link>
          </div>
        </div>
        
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-vegana-primary">Tu impacto positivo</h2>
          <ImpactBanner />
        </div>
        
        <div className="mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-5 shadow-md border border-purple-100 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-full bg-white/50 backdrop-blur-[2px] z-0"></div>
            
            <div className="absolute top-0 right-0 h-full w-1/2 bg-gradient-to-l from-purple-200/20 to-transparent z-0"></div>
            
            <div className="relative z-10 flex items-start gap-4">
              <div className="flex-1">
                <div className={`${isMobile ? 'block mb-4 text-center' : 'hidden'}`}>
                  <Avatar className="h-16 w-16 border-2 border-purple-300 shadow-lg inline-block bg-white">
                    <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
                    <AvatarFallback>L</AvatarFallback>
                  </Avatar>
                </div>
                
                <h3 className="text-xl font-medium text-vegana-primary mb-3 flex items-center justify-center sm:justify-start">
                  {dailyGreeting}
                  <Sparkles className="w-5 h-5 text-amber-500 ml-2" />
                </h3>
                <div className="flex flex-col sm:flex-row gap-3 mb-3 items-center sm:items-start">
                  <DateDisplay currentTime={currentTime} />
                </div>
                <div className="bg-purple-100/70 backdrop-blur-sm p-4 rounded-lg text-sm text-gray-700 mt-3 border border-purple-200/50 shadow-sm">
                  <p className="flex items-start">
                    <span className="font-medium mr-1">Consejo del día:</span> 
                    {personalizedTip}
                  </p>
                </div>
              </div>
              
              <div className={`flex justify-center ${isMobile ? 'hidden' : 'block'}`}>
                <Avatar className="h-16 w-16 border-2 border-purple-300 shadow-lg flex-shrink-0 bg-white">
                  <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
                  <AvatarFallback>L</AvatarFallback>
                </Avatar>
              </div>
            </div>
          </motion.div>
        </div>
        
        <div className="mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="rounded-xl overflow-hidden shadow-lg border border-teal-100"
          >
            <div className="bg-gradient-to-r from-teal-500 to-emerald-600 p-5">
              <div className="flex items-center gap-2 mb-1">
                <Award className="h-6 w-6 text-white" />
                <h3 className="text-xl font-bold text-white">Vital Score</h3>
              </div>
              <p className="text-teal-50 text-sm">Tu puntuación diaria de bienestar</p>
            </div>
            
            <div className="p-5 bg-gradient-to-b from-white to-teal-50">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex flex-col">
                  <div className="flex items-center mb-1">
                    <span className="text-4xl font-bold mr-2 text-teal-700">{scoreTotal}</span>
                    <div className={`flex items-center gap-1 ${scoreDisplay.color} bg-white/80 backdrop-blur-sm px-2 py-1 rounded-full shadow-sm`}>
                      <span className="text-lg mr-1">{scoreDisplay.emoji}</span> 
                      <span className="text-sm font-medium">{scoreDisplay.text}</span>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 md:mb-0 mt-1">
                    {personalizedTip}
                  </p>
                </div>
                
                <Link to="/diario-nutricional">
                  <Button variant="secondary" className="w-full md:w-auto">
                    Actualiza tu diario
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const Index = () => {
  const { user } = useAuth();
  
  return (
    <Layout>
      {user ? (
        <LoggedInDashboard user={user} />
      ) : (
        <LoggedOutHero />
      )}
    </Layout>
  );
};

export default Index;
